import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
} from "@mui/material";
import { statisticAuditCell, auditSort } from "../helpers/leagueStatisticAudit";
import { goalFieldLabel } from "../helpers/teamSettingValues";

// Shared MUI table conventions from TeamStatisticsTable: centered sortable numbers,
// fixed team column, horizontal scrolling and unrounded sort keys.
export default function LeagueStatisticAudit({
  data,
  proposed,
  ids,
  names,
  logo,
  statName,
  en,
}) {
  const t = (d, e) => (en ? e : d);
  const keys = [
    ...Object.keys(data.state?.reference?.statistics || {}),
    "moments",
    "conversion",
    "goals",
  ];
  const [metrics, setMetrics] = useState(["moments", "conversion", "goals"]),
    [fields, setFields] = useState(["home_for"]),
    [sources, setSources] = useState([
      "saved",
      "expected",
      "observed",
      "proposal",
    ]),
    [totals, setTotals] = useState(false),
    [sort, setSort] = useState(null),
    [search, setSearch] = useState("");
  const sourceLabels = {
    saved: t("Gespeichert · zu ändern", "Saved · editing baseline"),
    expected: t("Historisch erwartet", "Historical expectation"),
    observed: t("Erspielt / zugelassen", "Observed / conceded"),
    proposal: t("Vorschlag · normiert", "Proposal · normalized"),
  };
  const label = (k) =>
    k === "moments"
      ? "Attack Moments"
      : k === "conversion"
      ? "Conversion"
      : k === "goals"
      ? t("Torwert · AM × Conversion", "Goals · AM × conversion")
      : statName(k);
  const toggle = (setter, list, k) =>
    setter(list.includes(k) ? list.filter((v) => v !== k) : [...list, k]);
  const cols = keys
    .filter((k) => metrics.includes(k))
    .flatMap((key) =>
      fields.flatMap((field) =>
        sources.map((source) => ({
          key,
          field,
          source,
          id: [key, field, source].join(":"),
        }))
      )
    );
  const value = (id, c) =>
    statisticAuditCell(data, proposed, id, c.key, c.field, c.source, totals);
  const ordered = [...ids].sort((a, b) =>
    sort
      ? auditSort(value(a, sort), value(b, sort), sort.direction)
      : names(a).localeCompare(names(b))
  );
  const fmt = (v, key) =>
    Number.isFinite(v)
      ? `${(v * (["conversion", "45"].includes(key) ? 100 : 1)).toLocaleString(
          en ? "en-GB" : "de-DE",
          { maximumFractionDigits: 2, minimumFractionDigits: 2 }
        )}${["conversion", "45"].includes(key) ? " %" : ""}`
      : "—";
  const choices = (list, selected, setter, labels) =>
    list.map((k) => (
      <label
        key={k}
        style={{ display: "inline-block", margin: "6px 12px 6px 0" }}
      >
        <input
          type="checkbox"
          checked={selected.includes(k)}
          onChange={() => toggle(setter, selected, k)}
        />
        {labels(k)}
      </label>
    ));
  return (
    <section
      aria-label={t("Liga-Statistiken prüfen", "Review league statistics")}
    >
      <h3>
        {t(
          "Liga-Statistiken · alle Teams prüfen",
          "League statistics · review all teams"
        )}
      </h3>
      <label>
        {t("Erspielte / erwartete Werte: ", "Observed / expected values: ")}
        <select
          value={totals ? "sum" : "average"}
          onChange={(e) => setTotals(e.target.value === "sum")}
        >
          <option value="average">
            {t("Durchschnitt pro Spiel", "Average per match")}
          </option>
          <option value="sum">{t("Summe", "Total")}</option>
        </select>
      </label>
      <p>
        {t(
          "Gespeicherte Werte und Vorschläge bleiben Modellwerte pro Spiel. Conversion und Ballbesitz bleiben Prozentwerte. Historische Produkte verwenden die gespeicherten Tore und Attack Moments derselben Spiele; erspielte Conversion × Attack Moments ergibt tatsächliche Tore, nicht Should Goals.",
          "Saved values and proposals remain model values per match. Conversion and possession remain percentages. Historical products use goals and attack moments from the same matches; observed conversion × attack moments yields actual goals, not Should Goals."
        )}
      </p>
      <details>
        <summary>{t("Spalten auswählen", "Choose columns")}</summary>
        <div>
          {choices(
            ["home_for", "home_against", "away_for", "away_against"],
            fields,
            setFields,
            (k) => goalFieldLabel(k, en)
          )}
        </div>
        <div>
          {choices(
            Object.keys(sourceLabels),
            sources,
            setSources,
            (k) => sourceLabels[k]
          )}
        </div>
        <input
          aria-label={t("Statistik suchen", "Find statistic")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div>
          {choices(
            keys.filter((k) =>
              label(k).toLowerCase().includes(search.toLowerCase())
            ),
            metrics,
            setMetrics,
            label
          )}
        </div>
      </details>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ maxHeight: 650, overflow: "auto" }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 180 }}>
                {t("Mannschaft", "Team")}
              </TableCell>
              {cols.map((c) => (
                <TableCell
                  key={c.id}
                  align="center"
                  sx={{
                    minWidth: 155,
                    bgcolor:
                      c.source === "saved" ? "action.selected" : undefined,
                  }}
                >
                  <TableSortLabel
                    active={sort?.id === c.id}
                    direction={sort?.id === c.id ? sort.direction : "desc"}
                    onClick={() =>
                      setSort({
                        ...c,
                        direction:
                          sort?.id === c.id && sort.direction === "desc"
                            ? "asc"
                            : "desc",
                      })
                    }
                  >
                    {label(c.key)}
                    <br />
                    {goalFieldLabel(c.field, en)}
                    <br />
                    {sourceLabels[c.source]}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {ordered.map((id) => (
              <TableRow key={id}>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    position: "sticky",
                    left: 0,
                    bgcolor: "background.paper",
                    zIndex: 1,
                    fontWeight: 800,
                  }}
                >
                  {logo(id)}
                  {names(id)}
                </TableCell>
                {cols.map((c) => {
                  const v = value(id, c);
                  return (
                    <TableCell
                      key={c.id}
                      align="center"
                      sx={{
                        fontWeight: c.source === "saved" ? 900 : 400,
                        bgcolor:
                          c.source === "saved" ? "action.selected" : undefined,
                        borderLeft:
                          c.source === "saved"
                            ? "3px solid #65caff"
                            : undefined,
                      }}
                    >
                      <span>{fmt(v.value, c.key)}</span>
                      <details>
                        <summary>
                          {t("Herkunft / Rechnung", "Source / calculation")}
                        </summary>
                        {v.model ? (
                          <>
                            <p>{sourceLabels[c.source]}</p>
                            {["moments", "goals"].includes(c.key) && (
                              <>
                                <p>
                                  {t(
                                    "Modellrechnung gegen die Ligareferenz; Ballbesitz Für/Gegen wird wie im Modell auf zusammen 100 % gebracht.",
                                    "Model calculation against league reference; possession for/against is normalized to 100% as in the model."
                                  )}
                                </p>
                                {v.terms.map((term) => (
                                  <div key={term.key}>
                                    {statName(term.key)}:{" "}
                                    {fmt(term.value, term.key)} × {term.weight}{" "}
                                    ={" "}
                                    {fmt(
                                      Number.isFinite(term.value)
                                        ? term.value * term.weight
                                        : null,
                                      "moments"
                                    )}
                                  </div>
                                ))}
                                <p>
                                  Σ = {fmt(v.moments, "moments")}; ×{" "}
                                  {fmt(v.rate, "conversion")} ={" "}
                                  {fmt(
                                    Number.isFinite(v.moments) &&
                                      Number.isFinite(v.rate)
                                      ? v.moments * v.rate
                                      : null,
                                    "goals"
                                  )}
                                </p>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            <p>
                              {v.count}/{v.selected}{" "}
                              {t("Spiele mit Daten", "matches covered")}
                            </p>
                            {v.moments !== undefined && (
                              <p>
                                {t("Gepoolt: Tore", "Pooled: goals")}{" "}
                                {fmt(v.goals, "goals")} / AM{" "}
                                {fmt(v.moments, "moments")} ={" "}
                                {fmt(v.rate, "conversion")}
                              </p>
                            )}
                            <p>
                              {t(
                                "Gespeicherte historische Werte; keine Neuberechnung mit heutigen Settings.",
                                "Stored historical values; not recalculated using current settings."
                              )}
                            </p>
                            <ul>
                              {v.fixtures?.map((g) => (
                                <li key={g.id}>
                                  {g.home} − {g.away}
                                  {g.goals
                                    ? ` · ${g.goals.home}−${g.goals.away}`
                                    : ""}{" "}
                                  · ID {g.id}
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                      </details>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </section>
  );
}
